import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaEraser, FaSearch, FaUndo } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  buscarChequesParaEstorno,
  executarEstornoReaprCheque,
  listarTodosGruposEmpresa,
} from '~/Services/Service';
import ModalSelecionarCheque from './ModalSelecionarCheque';

const INITIAL_VALUES = {
  numero: '',
  banco: '',
  agencia: '',
  conta: '',
};

const MSG_GRUPO_BANCO = 'Informe banco, agência e conta juntos';

// .when() mútuo entre banco/agencia/conta gerava dependência cíclica em Yup
// (toposort lança "Cyclic dependency, node was: conta", quebra app inteiro).
// Validamos o grupo como um todo via .test() no objeto pai: se algum
// preenchido, todos têm que estar — criando erro em cada campo faltante
// para o Formik destacar.
const schemaBusca = Yup.object()
  .shape({
    numero: Yup.string().trim().required('Número do cheque é obrigatório'),
    banco: Yup.string(),
    agencia: Yup.string(),
    conta: Yup.string(),
  })
  .test('grupo-banco-completo', MSG_GRUPO_BANCO, function validar(values) {
    const { banco, agencia, conta } = values || {};
    const algumPreenchido = Boolean(banco || agencia || conta);
    const todosPreenchidos = Boolean(banco && agencia && conta);
    if (!algumPreenchido || todosPreenchidos) {
      return true;
    }
    const erros = [];
    if (!banco) erros.push(this.createError({ path: 'banco', message: MSG_GRUPO_BANCO }));
    if (!agencia) erros.push(this.createError({ path: 'agencia', message: MSG_GRUPO_BANCO }));
    if (!conta) erros.push(this.createError({ path: 'conta', message: MSG_GRUPO_BANCO }));
    return new Yup.ValidationError(erros);
  });

const formatarData = data => (data ? moment(data).format('DD/MM/YYYY') : '-');

const formatarMoeda = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const obterClasseErro = (errors, touched, campo) =>
  errors[campo] && touched[campo] ? 'is-invalid' : '';

const EstornoReaprCheque = () => {
  const formRef = useRef(null);
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [grupoEmpresaSelecionado, setGrupoEmpresaSelecionado] = useState(null);
  const [cheques, setCheques] = useState([]);
  const [chequeSelecionado, setChequeSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [loadingEstorno, setLoadingEstorno] = useState(false);

  useEffect(() => {
    const carregarGruposEmpresa = async () => {
      setLoadingGrupos(true);
      try {
        const res = await listarTodosGruposEmpresa();
        const opcoes = (res || []).map(ge => ({
          value: ge.codigo,
          label: `${ge.codigo} - ${ge.apelido}`,
        }));
        setGruposEmpresa(opcoes);
        if (opcoes.length === 1) {
          setGrupoEmpresaSelecionado(opcoes[0]);
        }
      } catch (err) {
        toast.error(extrairMensagemErro(err));
      } finally {
        setLoadingGrupos(false);
      }
    };

    carregarGruposEmpresa();
  }, []);

  const limparResultado = () => {
    setCheques([]);
    setChequeSelecionado(null);
    setShowModal(false);
  };

  const limparTudo = () => {
    limparResultado();
    if (formRef.current) {
      formRef.current.resetForm();
    }
  };

  const selecionarCheque = cheque => {
    if (!cheque.data_reapresentacao) {
      toast.error('Cheque ainda não tem reapresentação.');
      limparResultado();
      return;
    }

    setChequeSelecionado(cheque);
  };

  const handleBuscar = async values => {
    if (!grupoEmpresaSelecionado) {
      toast.error('Selecione o Grupo de Empresa.');
      return;
    }

    setLoadingBusca(true);
    limparResultado();

    try {
      const filtros = {
        numero: values.numero.trim(),
        banco: values.banco ? values.banco.trim() : null,
        agencia: values.agencia ? values.agencia.trim() : null,
        conta: values.conta ? values.conta.trim() : null,
        codigo_ge: grupoEmpresaSelecionado.value,
      };
      const resultado = await buscarChequesParaEstorno(filtros);
      const lista = resultado || [];
      setCheques(lista);

      if (lista.length === 0) {
        toast.error('Nenhum cheque encontrado.');
        return;
      }

      if (lista.length === 1) {
        selecionarCheque(lista[0]);
        return;
      }

      setShowModal(true);
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingBusca(false);
    }
  };

  const handleConfirmacao = async confirmado => {
    setShowConfirmacao(false);
    if (!confirmado || !chequeSelecionado) return;

    setLoadingEstorno(true);
    try {
      await executarEstornoReaprCheque({
        codigo_titulo: chequeSelecionado.codigo_titulo,
        codigo_ge: grupoEmpresaSelecionado.value,
      });
      toast.success('Lançamento efetuado com sucesso.');
      limparTudo();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingEstorno(false);
    }
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Estorno de Reapresentação de Cheques" />

      <Card className="shadow-sm mb-3">
        <Card.Header>Grupo de Empresa</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6} lg={4}>
              <Form.Label>Grupo de Empresa</Form.Label>
              <Select
                options={gruposEmpresa}
                value={grupoEmpresaSelecionado}
                onChange={setGrupoEmpresaSelecionado}
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder={loadingGrupos ? 'Carregando...' : 'Selecione...'}
                isLoading={loadingGrupos}
                isDisabled={loadingGrupos}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-3">
        <Card.Header>Pesquisar Cheque</Card.Header>
        <Card.Body>
          <Formik
            innerRef={formRef}
            initialValues={INITIAL_VALUES}
            validationSchema={schemaBusca}
            onSubmit={handleBuscar}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label>Número do Cheque*</Form.Label>
                      <Form.Control
                        name="numero"
                        value={values.numero}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={obterClasseErro(errors, touched, 'numero')}
                      />
                      {errors.numero && touched.numero ? (
                        <div className="invalid-feedback d-block">{errors.numero}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label>Banco</Form.Label>
                      <Form.Control
                        name="banco"
                        value={values.banco}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={obterClasseErro(errors, touched, 'banco')}
                      />
                      {errors.banco && touched.banco ? (
                        <div className="invalid-feedback d-block">{errors.banco}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label>Agência</Form.Label>
                      <Form.Control
                        name="agencia"
                        value={values.agencia}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={obterClasseErro(errors, touched, 'agencia')}
                      />
                      {errors.agencia && touched.agencia ? (
                        <div className="invalid-feedback d-block">{errors.agencia}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label>Conta</Form.Label>
                      <Form.Control
                        name="conta"
                        value={values.conta}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={obterClasseErro(errors, touched, 'conta')}
                      />
                      {errors.conta && touched.conta ? (
                        <div className="invalid-feedback d-block">{errors.conta}</div>
                      ) : null}
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex flex-column flex-sm-row justify-content-end">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="mb-2 mb-sm-0 mr-sm-2"
                    onClick={limparTudo}
                    disabled={loadingBusca || loadingEstorno}
                  >
                    <FaEraser className="mr-1" /> Limpar
                  </Button>
                  <Button type="submit" variant="primary" disabled={loadingBusca || loadingEstorno}>
                    {loadingBusca ? (
                      <Spinner animation="border" size="sm" className="mr-1" />
                    ) : (
                      <FaSearch className="mr-1" />
                    )}
                    Pesquisar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      {chequeSelecionado ? (
        <Card className="shadow-sm mb-3">
          <Card.Header>Dados do Cheque</Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Cedente</Form.Label>
                  <Form.Control value={chequeSelecionado.cgc_cpf || '-'} readOnly />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Vencimento</Form.Label>
                  <Form.Control value={formatarData(chequeSelecionado.data_vencimento)} readOnly />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Valor</Form.Label>
                  <Form.Control value={formatarMoeda(chequeSelecionado.valor)} readOnly />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Portador</Form.Label>
                  <Form.Control value={chequeSelecionado.apelido_conta || '-'} readOnly />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Data Reapresentação</Form.Label>
                  <Form.Control value={formatarData(chequeSelecionado.data_reapresentacao)} readOnly />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Data Devolução</Form.Label>
                  <Form.Control value={formatarData(chequeSelecionado.data_devolucao)} readOnly />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : null}

      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="success"
          onClick={() => setShowConfirmacao(true)}
          disabled={!chequeSelecionado || loadingEstorno}
        >
          {loadingEstorno ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <FaUndo className="mr-1" />
          )}
          Lançar Estorno
        </Button>
      </div>

      <ModalSelecionarCheque
        show={showModal}
        cheques={cheques}
        onSelect={selecionarCheque}
        onHide={() => setShowModal(false)}
      />

      <ModalConfirmacao
        show={showConfirmacao}
        title="Confirmar estorno"
        message={
          chequeSelecionado
            ? `Confirmar estorno da reapresentação do cheque ${chequeSelecionado.numero}?`
            : 'Confirmar estorno da reapresentação?'
        }
        variant="danger"
        onClose={handleConfirmacao}
      />
    </Container>
  );
};

export default EstornoReaprCheque;
